import { FC, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Form, Input, Button, Checkbox } from "antd";
import { EyeInvisibleOutlined, EyeOutlined } from '@ant-design/icons';
import { AlertMessage } from "../../components/common/AlertMessage";
import { useAlert } from "../../hooks/useAlert";
import { ALERT_Z_INDEX } from "../../utils/alertManager";
import { postRegisterApi } from "../../service/login";
import { APP_NAME, COPYRIGHT_TEXT, SHANHAI_LOGO } from "../../config/constants";
import "./Register.scss";
import "../../styles/reset.scss";

interface Props {}

export const Register: FC<Props> = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [agreeTerms, setAgreeTerms] = useState(false);
  const { alertConfig, showAlert, hideAlert, showSuccess, showError, showWarning, showInfo } = useAlert();

  const onFinish = async (formData: any) => {
    // 移除console.log调试信息
    
    // 表单验证
    if (!formData.username || !formData.username.trim()) {
      showError('请输入账号');
      return;
    }
    
    // if (!formData.email || !formData.email.trim()) {
    //   showError('请输入邮箱');
    //   return;
    // }
    
    if (!formData.phone || !formData.phone.trim()) {
      showError('请输入手机号');
      return;
    }
    
    if (!formData.password || !formData.password.trim()) {
      showError('请输入密码');
      return;
    }
    
    if (!formData.confirmPassword || !formData.confirmPassword.trim()) {
      showError('请重复输入密码');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      showError('两次输入的密码不一致');
      return;
    }
    
    // if (!agreeTerms) {
    //   showError('请阅读并同意服务条款和隐私政策');
    //   return;
    // }
    
    setIsLoading(true);
    
    try {
      // 调用注册API
      const registerData = {
        username: formData.username,
        phone: formData.phone, // 使用 phone 字段，API 内部会转换为 mobile
        password: formData.password
      };
      
      const response = await postRegisterApi(registerData);
      
      // 检查响应状态
      if (response && response.code === 0) {
        // 注册成功
        // 设置注册成功标记，用于登录页显示提示
        localStorage.setItem("registerSuccess", "true");
        localStorage.setItem("registerSuccessMessage", "注册成功！请登录您的账户");
        
        // 直接跳转到登录页
        navigate("/login");
      } else {
        // 注册失败，显示 msg 中的内容
        const errorMessage = response?.msg || '注册失败，请稍后重试';
        showError(errorMessage);
      }
      
    } catch (error: any) {
      // 优先显示响应中的 msg 信息，如果没有则显示 detail，最后显示 message
      const errorMessage = error?.response?.msg || error?.msg || error?.detail || error?.message || '注册失败，请稍后重试';
      showError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogin = () => {
    navigate("/login");
  };

  const handleTermsClick = () => {
    showInfo('服务条款功能开发中...');
  };

  const handlePrivacyClick = () => {
    showInfo('隐私政策功能开发中...');
  };

  return (
    <div className="register-page">
      {/* Alert提示组件 - 页面顶部 */}
      <AlertMessage 
        config={alertConfig}
        onClose={hideAlert}
        zIndex={ALERT_Z_INDEX.REGISTER}
      />

              {/* 左上角Logo和标题 */}
        <div className="header-logo">
          <div className="logo-container">
            <div className="logo-icon">
              <img src={SHANHAI_LOGO} alt={APP_NAME} className="logo-img" />
            </div>
            <h1 className="logo-title">{APP_NAME}</h1>
          </div>
        </div>

      <div className="register-container">
        {/* 注册表单 */}
        <div className="register-form">
          {/* 标题区域 */}
          <div className="form-header">
            <h2 className="form-title">注册</h2>
            <div className="login-link">
              <span>已有账号?</span>
              <a href="#" className="login-now" onClick={handleLogin}>立即登录</a>
            </div>
          </div>

          <Form onFinish={onFinish}>
            {/* 账号设置 */}
            <div className="form-group">
              <label htmlFor="username" className="form-label">
                账号设置
              </label>
              <Form.Item 
                name="username" 
                className="form-item-wrapper"
                rules={[
                  { required: true, message: '请输入账号' },
                  { min: 4, message: '账号不少于四位' },
                  { max: 20, message: '账号最多20个字符' }
                ]}
              >
                <Input 
                  placeholder="请输入账号"
                  className="form-input username-input"
                  disabled={isLoading}
                />
              </Form.Item>
            </div>

            {/* 邮箱设置 */}
            {/* <div className="form-group">
              <label htmlFor="email" className="form-label">
                邮箱设置
              </label>
              <Form.Item 
                name="email" 
                className="form-item-wrapper"
                rules={[
                  { required: true, message: '请输入邮箱' },
                  { type: 'email', message: '请输入正确的邮箱格式' }
                ]}
              >
                <Input 
                  placeholder="请输入邮箱"
                  className="form-input email-input"
                  disabled={isLoading}
                />
              </Form.Item>
            </div> */}

            {/* 手机号设置 */}
            <div className="form-group">
              <label htmlFor="phone" className="form-label">
                手机号码
              </label>
              <Form.Item 
                name="phone" 
                className="form-item-wrapper"
                rules={[
                  { required: true, message: '请输入手机号' },
                  { pattern: /^1[3-9]\d{9}$/, message: '请输入正确的手机号格式' }
                ]}
              >
                <Input 
                  placeholder="请输入手机号"
                  className="form-input phone-input"
                  disabled={isLoading}
                />
              </Form.Item>
            </div>

            {/* 设置密码 */}
            <div className="form-group">
              <label htmlFor="password" className="form-label">
                设置密码
              </label>
              <Form.Item 
                name="password" 
                className="form-item-wrapper"
                rules={[
                  { required: true, message: '请输入密码' },
                  { min: 6, message: '密码至少6个字符' },
                  { max: 20, message: '密码最多20个字符' }
                ]}
              >
                <Input.Password
                  placeholder="请输入密码"
                  className="form-input password-input"
                  disabled={isLoading}
                  iconRender={(visible) => (
                    visible ? (
                      <EyeOutlined style={{ color: '#8bb3d9' }} />
                    ) : (
                      <EyeInvisibleOutlined style={{ color: '#8bb3d9' }} />
                    )
                  )}
                />
              </Form.Item>
            </div>

            {/* 重复密码 */}
            <div className="form-group">
              <label htmlFor="confirmPassword" className="form-label">
                重复密码
              </label>
              <Form.Item 
                name="confirmPassword" 
                className="form-item-wrapper"
                rules={[
                  { required: true, message: '请重复输入密码' },
                  { min: 6, message: '密码至少6个字符' },
                  { max: 20, message: '密码最多20个字符' }
                ]}
              >
                <Input.Password
                  placeholder="请重复输入密码"
                  className="form-input confirm-password-input"
                  disabled={isLoading}
                  iconRender={(visible) => (
                    visible ? (
                      <EyeOutlined style={{ color: '#8bb3d9' }} />
                    ) : (
                      <EyeInvisibleOutlined style={{ color: '#8bb3d9' }} />
                    )
                  )}
                />
              </Form.Item>
            </div>

            {/* 服务条款同意 */}
            {/* <div className="form-terms">
              <Form.Item name="agreeTerms" valuePropName="checked" className="terms-item">
                <Checkbox 
                  checked={agreeTerms}
                  onChange={(e) => setAgreeTerms(e.target.checked)}
                  className="terms-checkbox"
                >
                  我已阅读并同意
                  <a href="#" className="terms-link" onClick={handleTermsClick}>《服务条款》</a>
                  和
                  <a href="#" className="terms-link" onClick={handlePrivacyClick}>《隐私政策》</a>
                </Checkbox>
              </Form.Item>
            </div> */}

            {/* 注册按钮 */}
            <Form.Item className="form-item-wrapper">
              <Button 
                htmlType="submit"
                className={`register-btn ${isLoading ? 'loading' : ''}`}
                disabled={isLoading}
                loading={isLoading}
              >
                {isLoading ? '注册中...' : '注册账号'}
              </Button>
            </Form.Item>
          </Form>
        </div>
      </div>

      {/* 页脚版权信息 */}
      <div className="footer-copyright">
        {COPYRIGHT_TEXT}
      </div>
    </div>
  );
};
